import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { encryptId } from '../../helper/helper';
import PanelTopSecond from '../../component/Admin/PanelTopSecond';
import LoadingSmall from '../../component/LoadingSmall';
import { Document, Page, pdfjs } from 'react-pdf';
import {
    FaDownload,
    FaEnvelope,
    FaExternalLinkAlt,
    FaFileInvoice,
    FaPrint,
    FaEdit,
    FaLock,
} from 'react-icons/fa';
import { canAccess } from '../../helper/permissions';

const LoaDetail = ({ data }) => {
    const { loaId } = useParams();
    const navigate = useNavigate();

    const header = {
        headers: { Authorization: `Bearer ${data.token}` },
    };
    const canEdit = canAccess(data?.user?.permissions || {}, 'loa', 'edit');
    const canEditPayment =
        canAccess(data?.user?.permissions || {}, 'payment', 'edit') || canEdit;

    const [loaDetail, setLoaDetail] = useState(null);
    const [detailPayment, setDetailPayment] = useState(null);
    const [pdfUrl, setPdfUrl] = useState('');
    const [numPages, setNumPages] = useState(null);
    const [isLoadingPdf, setIsLoadingPdf] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isPaymentActionLoading, setIsPaymentActionLoading] = useState(false);
    const [isSendLoaLoading, setIsSendLoaLoading] = useState(false);
    const [pdfWidth, setPdfWidth] = useState(640);
    const loadedLoaIdRef = useRef(null);
    const pdfContainerRef = useRef(null);

    useEffect(() => {
        const container = pdfContainerRef.current;
        if (!container) return undefined;

        const updateWidth = () => {
            const nextWidth = Math.floor(container.clientWidth);
            if (nextWidth > 0) {
                setPdfWidth(nextWidth);
            }
        };

        updateWidth();
        const observer = new ResizeObserver(updateWidth);
        observer.observe(container);
        window.addEventListener('resize', updateWidth);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateWidth);
        };
    }, [pdfUrl]);

    useEffect(() => {
        try {
            pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;
        } catch (_) { }
    }, []);

    useEffect(() => {
        if (loaId && loadedLoaIdRef.current !== loaId) {
            loadedLoaIdRef.current = loaId;
            loadDetail(loaId);
        }

        return () => {
            if (pdfUrl) {
                try {
                    URL.revokeObjectURL(pdfUrl);
                } catch (_) { }
            }
        };
        // eslint-disable-next-line
    }, [loaId]);

    const loadDetail = async (id) => {
        setIsLoading(true);
        setPdfUrl('');
        setNumPages(null);
        setDetailPayment(null);

        try {
            const detailRes = await axios.get(`api/loa/detail/${id}`, header);
            const detail = detailRes.data;
            setLoaDetail(detail);
            setDetailPayment(detail.payment || null);
            setIsLoading(false);
            loadPdf(id);
        } catch (error) {
            console.error('Error loading LOA detail:', error);
            setIsLoading(false);
            Swal.fire('Gagal', 'Tidak dapat memuat detail LOA', 'error').then(() => {
                navigate('/admin/loa');
            });
        }
    };

    const loadPdf = async (id) => {
        try {
            setIsLoadingPdf(true);
            const res = await axios.get(`api/loa/pdf/${encryptId(`${id}`)}`, {
                ...header,
                responseType: 'blob',
                timeout: 30000,
            });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            setPdfUrl(URL.createObjectURL(blob));
        } catch (error) {
            console.error('Error loading LOA PDF:', error);
            setPdfUrl('');
        } finally {
            setIsLoadingPdf(false);
        }
    };

    const handleDownloadInvoice = async () => {
        if (!detailPayment?.trxID) return;
        try {
            setIsPaymentActionLoading(true);
            const response = await axios.get(
                `api/payment/invoice/pdf/${detailPayment.trxID}`,
                { ...header, responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(
                new Blob([response.data], { type: 'application/pdf' })
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Invoice_${detailPayment.trxID}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
            Swal.fire('Gagal', 'Download invoice gagal', 'error');
        } finally {
            setIsPaymentActionLoading(false);
        }
    };

    const handleSendInvoiceEmail = async () => {
        if (!detailPayment?.trxID) return;
        const pm = detailPayment.paymentMethodID || {};
        const payload = {
            customerName: detailPayment.customerName,
            customerEmail: detailPayment.customerEmail,
            customerPhone: detailPayment.customerPhone,
            articleName: detailPayment.articleName,
            journalName: detailPayment.journalName,
            customerAddress: detailPayment.customerAddress,
            paymentName: detailPayment.paymentName,
            trxID: detailPayment.trxID,
            amount: detailPayment.amount,
            bank: pm.bank || detailPayment.bank,
            owner: pm.owner || detailPayment.owner,
            rekening_number: pm.rekening_number || detailPayment.rekening_number,
            createAt: detailPayment.createAt,
        };
        const endpoint =
            detailPayment.paymentStatus === 'lunas'
                ? 'api/payment/sendMailInvoice'
                : 'api/payment/sendMailBill';
        try {
            setIsPaymentActionLoading(true);
            await axios.post(endpoint, payload, header);
            Swal.fire('Berhasil', 'Email berhasil dikirim', 'success');
        } catch (error) {
            console.error(error);
            Swal.fire('Gagal', 'Gagal mengirim email', 'error');
        } finally {
            setIsPaymentActionLoading(false);
        }
    };

    const handleSendLoaEmail = async () => {
        if (!loaId) return;
        if (detailPayment?.paymentStatus !== 'lunas') {
            Swal.fire('Tidak bisa kirim', 'LOA hanya bisa dikirim jika payment sudah lunas.', 'warning');
            return;
        }
        if (!detailPayment?.customerEmail) {
            Swal.fire(
                'Tidak bisa kirim',
                'Email customer belum tersedia. Hubungkan payment yang punya email dulu.',
                'warning'
            );
            return;
        }

        const confirm = await Swal.fire({
            title: 'Send LOA?',
            html: `Kirim PDF LOA ke<br/><b>${detailPayment.customerEmail}</b>?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Kirim',
            cancelButtonText: 'Batal',
            confirmButtonColor: '#4569ab',
        });
        if (!confirm.isConfirmed) return;

        try {
            setIsSendLoaLoading(true);
            const res = await axios.post(`api/loa/sendMail/${loaId}`, {}, header);
            Swal.fire(
                'Berhasil',
                `PDF LOA terkirim ke ${res.data?.to || detailPayment.customerEmail}`,
                'success'
            );
        } catch (error) {
            console.error(error);
            const msg = error?.response?.data?.message || 'Gagal mengirim email LOA';
            Swal.fire('Gagal', Array.isArray(msg) ? msg.join(', ') : msg, 'error');
        } finally {
            setIsSendLoaLoading(false);
        }
    };

    const handleSetPaymentStatus = async (status) => {
        if (!detailPayment?.trxID) return;
        try {
            setIsPaymentActionLoading(true);
            await axios.patch(
                `api/payment/transaction/${detailPayment.trxID}`,
                { paymentStatus: status },
                header
            );
            setDetailPayment((prev) => ({ ...prev, paymentStatus: status }));
            Swal.fire('Berhasil', `Status payment diubah menjadi ${status}`, 'success');
        } catch (error) {
            console.error(error);
            const msg = error?.response?.data?.message || 'Gagal mengubah status payment';
            Swal.fire('Gagal', Array.isArray(msg) ? msg.join(', ') : msg, 'error');
        } finally {
            setIsPaymentActionLoading(false);
        }
    };

    const handlePrintInvoice = () => {
        if (!detailPayment) return;
        const w = window.open('', '_blank');
        if (!w) return;
        const amount = new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
        }).format(detailPayment.amount || 0);
        w.document.write(`
            <html><head><title>Invoice ${detailPayment.trxID}</title>
            <style>body{font-family:sans-serif;padding:24px} table{width:100%;border-collapse:collapse} td{padding:6px 0}</style>
            </head><body>
            <h2>${detailPayment.paymentStatus === 'lunas' ? 'INVOICE' : 'TAGIHAN'}</h2>
            <table>
              <tr><td>Trx ID</td><td><b>${detailPayment.trxID}</b></td></tr>
              <tr><td>Artikel</td><td>${detailPayment.articleName || '-'}</td></tr>
              <tr><td>Journal</td><td>${detailPayment.journalName || '-'}</td></tr>
              <tr><td>Customer</td><td>${detailPayment.customerName || '-'}</td></tr>
              <tr><td>Email</td><td>${detailPayment.customerEmail || '-'}</td></tr>
              <tr><td>Amount</td><td><b>${amount}</b></td></tr>
              <tr><td>Status</td><td>${detailPayment.paymentStatus || '-'}</td></tr>
            </table>
            <script>window.print()</script>
            </body></html>
        `);
        w.document.close();
    };

    const journalName =
        loaDetail?.journal_id?.journal_name ||
        loaDetail?.journal_name ||
        loaDetail?.journalName ||
        '-';

    const downloadFileName = `${loaDetail?.submitID ? `${loaDetail.submitID}_` : ''}${(
        loaDetail?.title || 'loa'
    )
        .toString()
        .trim()
        .replace(/\s+/g, '_')
        .slice(0, 60)}.pdf`;

    const formatIDR = (amount) =>
        new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            maximumFractionDigits: 0,
        }).format(amount || 0);

    if (isLoading) {
        return (
            <>
                <PanelTopSecond link="/admin/loa" title="Detail LOA" />
                <div className="card flex min-h-[420px] flex-col items-center justify-center">
                    <LoadingSmall />
                    <p className="mt-4 text-sm text-muted">Memuat detail LOA...</p>
                </div>
            </>
        );
    }

    if (!loaDetail) {
        return (
            <>
                <PanelTopSecond link="/admin/loa" title="Detail LOA" />
                <div className="card card-body text-center text-muted">Data LOA tidak ditemukan.</div>
            </>
        );
    }

    const isLunas = detailPayment?.paymentStatus === 'lunas';
    const isPending = !detailPayment || detailPayment.paymentStatus === 'pending';

    return (
        <>
            <PanelTopSecond link="/admin/loa" title="Detail LOA" />

            <div className="space-y-4">
                <div className="card">
                    <div className="card-body">
                        <h2 className="text-base text-left font-semibold leading-snug text-ink dark:text-white md:text-lg">
                            {loaDetail.title || '-'}
                        </h2>
                        <p className=" text-left mt-1.5 text-sm text-muted">
                            <span className="font-mono text-body dark:text-white/80">
                                {loaDetail.submitID || `LOA${loaId}`}
                            </span>
                            {' · '}
                            {journalName}
                            {detailPayment ? (
                                <>
                                    {' · '}
                                    <span className={isPending ? 'text-accent-brand' : 'text-primary'}>
                                        {isLunas ? <FaLock className="mr-1 inline h-3 w-3" /> : null}
                                        {detailPayment.paymentStatus || 'pending'}
                                    </span>
                                </>
                            ) : null}
                        </p>
                    </div>
                </div>



                <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr_300px]">
                    <section className=" card overflow-hidden">
                        <div className="card">
                            <div className="card-body flex justify-end flex-wrap gap-2">
                                {isPending && canEdit ? (
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/admin/loa/${loaId}/edit`)}
                                        className="btn-outline-accent !min-h-9 !px-4 !text-sm"
                                    >
                                        <FaEdit className="h-3.5 w-3.5" />
                                        Edit
                                    </button>
                                ) : null}
                                {pdfUrl ? (
                                    <>
                                        <a
                                            href={pdfUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn-outline-accent !min-h-9 !px-4 !text-sm"
                                        >
                                            <FaExternalLinkAlt className="h-3.5 w-3.5" />
                                            Buka
                                        </a>
                                        <a
                                            href={pdfUrl}
                                            download={downloadFileName}
                                            className="btn-primary !min-h-9 !px-4 !text-sm"
                                        >
                                            <FaDownload className="h-3.5 w-3.5" />
                                            Download
                                        </a>
                                    </>
                                ) : null}
                                {!isPending ? (
                                    <button
                                        type="button"
                                        disabled={isSendLoaLoading || isLoadingPdf || !isLunas}
                                        onClick={handleSendLoaEmail}
                                        className="btn-primary !min-h-9 !px-4 !text-sm disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <FaEnvelope className="h-3.5 w-3.5" />
                                        {isSendLoaLoading ? 'Mengirim...' : 'Send'}
                                    </button>
                                ) : null}
                            </div>
                        </div>
                        <div className="bg-surface-soft p-4 dark:bg-black/20 md:p-0">
                            {isLoadingPdf ? (
                                <div className="flex min-h-[400px] flex-col items-center justify-center gap-3">
                                    <LoadingSmall />
                                    <p className="text-sm text-muted">Menyiapkan dokumen LOA...</p>
                                </div>
                            ) : pdfUrl ? (
                                <div ref={pdfContainerRef} className="w-full">
                                    <Document
                                        className="w-full overflow-hidden bg-canvas"
                                        file={pdfUrl}
                                        onLoadSuccess={({ numPages: pages }) => setNumPages(pages)}
                                        loading={
                                            <div className="flex min-h-[400px] flex-col items-center justify-center gap-3">
                                                <LoadingSmall />
                                                <p className="text-sm text-muted">Menampilkan PDF...</p>
                                            </div>
                                        }
                                        error={
                                            <div className="flex min-h-[420px] items-center justify-center p-6 text-sm text-error">
                                                Gagal menampilkan PDF.
                                            </div>
                                        }
                                    >
                                        <Page
                                            pageNumber={1}
                                            width={pdfWidth}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                        />
                                    </Document>
                                    {numPages && (
                                        <p className="px-4 py-3 text-center text-xs text-muted md:px-6">
                                            Halaman 1 dari {numPages}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="flex min-h-[300px] flex-col items-center justify-center text-center">
                                    <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-md bg-surface-soft text-muted dark:bg-white/5">
                                        <FaFileInvoice className="text-2xl" />
                                    </div>
                                    <p className="font-medium text-ink dark:text-white">PDF belum tersedia</p>
                                    <p className="mt-1 text-xs text-muted">Coba muat ulang halaman ini.</p>
                                </div>
                            )}
                        </div>
                    </section>

                    <aside className="space-y-4">
                        <section className="card">
                            <div className="card-body">
                                <h3 className="section-title mb-3">Informasi</h3>
                                <dl className="space-y-2.5 text-sm">
                                    <div className="flex justify-between gap-3">
                                        <dt className="text-muted">Penulis</dt>
                                        <dd className="min-w-0 text-right text-body dark:text-white/90">{loaDetail.author || '-'}</dd>
                                    </div>
                                    <div className="flex justify-between gap-3">
                                        <dt className="text-muted">Afiliasi</dt>
                                        <dd className="min-w-0 text-right text-body dark:text-white/90">{loaDetail.affiliate || '-'}</dd>
                                    </div>
                                    <div className="flex justify-between gap-3 border-t border-hairline pt-2.5 dark:border-white/10">
                                        <dt className="text-muted">Volume / Issue</dt>
                                        <dd className="font-medium text-ink dark:text-white">
                                            {loaDetail.volume || '-'} / {loaDetail.issue || '-'}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </section>

                        <section className="card">
                            <div className="card-body">
                                <h3 className="section-title mb-3">Payment</h3>
                                {detailPayment ? (
                                    <div className="space-y-3 text-sm">
                                        <div className="flex items-center justify-between">
                                            <span className="text-muted">Total</span>
                                            <span className="font-semibold text-ink dark:text-white">
                                                {formatIDR(detailPayment.amount)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between gap-3">
                                            <span className="text-muted">Customer</span>
                                            <span className="min-w-0 text-right">{detailPayment.customerName || '-'}</span>
                                        </div>
                                        <div className="flex justify-between gap-3">
                                            <span className="text-muted">Email</span>
                                            <span className="min-w-0 break-all text-right text-xs">
                                                {detailPayment.customerEmail || '-'}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-2 border-t border-hairline pt-3 dark:border-white/10">
                                            <button
                                                type="button"
                                                disabled={isPaymentActionLoading}
                                                onClick={handleDownloadInvoice}
                                                className="btn-outline-accent !min-h-8 !px-3 !text-xs"
                                            >
                                                <FaDownload className="h-3 w-3" /> Invoice
                                            </button>
                                            <button
                                                type="button"
                                                disabled={isPaymentActionLoading}
                                                onClick={handleSendInvoiceEmail}
                                                className="btn-outline-accent !min-h-8 !px-3 !text-xs"
                                            >
                                                <FaEnvelope className="h-3 w-3" /> Email
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handlePrintInvoice}
                                                className="btn-outline-accent !min-h-8 !px-3 !text-xs"
                                            >
                                                <FaPrint className="h-3 w-3" /> Cetak
                                            </button>
                                        </div>
                                        {canEditPayment ? (
                                            <button
                                                type="button"
                                                disabled={isPaymentActionLoading}
                                                onClick={() =>
                                                    handleSetPaymentStatus(isLunas ? 'pending' : 'lunas')
                                                }
                                                className={`w-full !min-h-9 !text-xs ${isLunas ? 'btn-secondary' : 'btn-primary'}`}
                                            >
                                                {isPaymentActionLoading
                                                    ? 'Memproses...'
                                                    : isLunas
                                                        ? 'Ubah ke Pending'
                                                        : 'Tandai Lunas'}
                                            </button>
                                        ) : null}
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted">Belum ada payment terhubung.</p>
                                )}
                            </div>
                        </section>
                    </aside>
                </div>
            </div>
        </>
    );
};

export default LoaDetail;
